/* Money in and out — STORY-020.
 *
 * ── the amount never becomes a float, at any point on this wire ─────────────
 * It travels as an integer of MINOR units plus an ISO 4217 code, exactly as it
 * is stored (ADR-0005). The client parses what somebody typed into minor units
 * at its boundary and formats them back for display; nothing in between ever
 * holds a decimal number. A float would be fine for a while and then quietly
 * wrong, which is the failure this shape exists to make impossible.
 *
 * ── the digit count is never assumed to be two ──────────────────────────────
 * JPY has none, KWD has three. Both sides read it from the currency.
 */
import { EVENT_STATUSES } from '@eventuley/domain';
import * as z from 'zod';
/**
 * Which way the money goes.
 *
 * One signed Direction, not two columns: separate income and expense columns
 * make it possible to write both on one row, and here that is inexpressible.
 */
export const TRANSACTION_DIRECTIONS = ['income', 'expense'];
/** Long enough for a real description, short enough to render in a list row. */
export const TRANSACTION_DESCRIPTION_MAX = 200;
/** A supplier, an employee, a guest. Free text, and personal data. */
export const COUNTERPARTY_MAX = 120;
export const CATEGORY_NAME_MAX = 60;
const description = z
    .string()
    .trim()
    .min(1, 'A description is needed.')
    .max(TRANSACTION_DESCRIPTION_MAX);
/**
 * Who the money moved with. Optional, and **empty means absent**.
 *
 * The coercion is the point. A form clears a field by submitting `""`, and
 * without this the boundary stored an empty string where every other "no
 * counterparty" row holds NULL. Two representations of the same fact is one
 * too many: the screen's `counterparty ? … : null` test then distinguishes
 * them only by accident, and a future `WHERE counterparty IS NULL` would miss
 * half the rows it was written for.
 */
const counterparty = z
    .string()
    .trim()
    .max(COUNTERPARTY_MAX)
    .transform((value) => (value === '' ? null : value));
/**
 * A whole number of minor units, always positive.
 *
 * Positive because the Direction carries the sign — a negative amount is a
 * contradiction rather than an expense. Zero is refused too: a zero-value row
 * reads as a placeholder somebody meant to come back to, not as a fact about
 * money that moved. EPIC-004 OQ2, settled that way here.
 *
 * `int()` rather than a decimal with two places, because the unit IS the minor
 * unit — 1234 is £12.34 and ¥1234, and which one depends on the currency
 * beside it, never on this number.
 */
const amountMinor = z
    .number()
    .int('An amount in whole minor units.')
    .positive('An amount above zero.');
/** Three letters. Whether the runtime knows it is checked at the boundary. */
const currency = z.string().trim().length(3).toUpperCase();
/**
 * A calendar date, not an instant — the day money moved, where it moved.
 *
 * Parsed as well as matched, exactly as `events.ts` does and for the same
 * reason it says so at length: the shape alone accepts `2026-02-31` and
 * `2026-13-01`, which reach the `date` column and make Postgres raise. That is
 * an unexpected throw, which the error boundary turns into an opaque 500 with a
 * logged stack — and a client's malformed input has to be a 400.
 *
 * Round-tripped rather than fed to `new Date`, which rolls February 31st
 * forward to March 3rd rather than refusing it.
 */
const occurredOn = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date, as YYYY-MM-DD.')
    .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return false;
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day);
}, 'That is not a date on any calendar.');
export const categorySchema = z.object({
    id: z.string(),
    direction: z.enum(TRANSACTION_DIRECTIONS),
    /** The customer's own wording. Seeded, then theirs — never a message key. */
    name: z.string(),
});
export const categoriesResponseSchema = z.object({
    categories: z.array(categorySchema),
});
/**
 * A name, trimmed, and belonging to one Direction — STORY-021.
 *
 * Trimmed here rather than only at the database, because " Kitchen" and
 * "Kitchen" are the same category to everyone except a byte comparison, and
 * the whole story is about not letting one idea become three rows.
 */
const categoryName = z.string().trim().min(1, 'A name is needed.').max(CATEGORY_NAME_MAX);
export const createCategoryRequestSchema = z.object({
    name: categoryName,
    /**
     * A Category belongs to one Direction and cannot be used by the other.
     *
     * The source workbook had `Satış` on both sides, which is how an income
     * total came to include expenses — so the Direction is chosen when the
     * Category is made, not per Transaction.
     */
    direction: z.enum(TRANSACTION_DIRECTIONS),
});
/**
 * Renaming only.
 *
 * The Direction is deliberately not changeable: moving a Category to the other
 * side would silently move every Transaction recorded under it, flipping money
 * between income and expense without anybody asking for that. Make a new one
 * and re-file, which is visible.
 */
export const updateCategoryRequestSchema = z.object({
    name: categoryName,
});
export const transactionSchema = z.object({
    id: z.string(),
    occurredOn: z.string(),
    direction: z.enum(TRANSACTION_DIRECTIONS),
    amountMinor: z.number().int(),
    currency: z.string(),
    category: categorySchema,
    /**
     * Null means **Overhead** — a real and expected case, not a missing value.
     *
     * STORY-023's reconciliation depends on it being allowed: attributed plus
     * overhead must equal the organisation total, exactly, and that identity only
     * holds if a Transaction is permitted to belong to no Event.
     */
    event: z.object({ id: z.string(), name: z.string() }).nullable(),
    counterparty: z.string().nullable(),
    description: z.string(),
    createdAt: z.string(),
});
export const TRANSACTIONS_PAGE_SIZE = 25;
export const TRANSACTIONS_PAGE_MAX = 200;
export const transactionsResponseSchema = z.object({
    transactions: z.array(transactionSchema),
    /** How many match the filters in total, so a client can page honestly. */
    total: z.number().int(),
    /**
     * Totals over the WHOLE filtered set, never the page — the same rule the
     * guest list follows, for the same reason: a figure that changed when
     * somebody clicked "next" would be a defect rather than an approximation.
     *
     * One entry per currency in use. Money in different currencies does not add
     * up, and a single number would either be a lie or require a conversion rate
     * this product does not have.
     */
    totals: z.array(z.object({
        currency: z.string(),
        incomeMinor: z.number().int(),
        expenseMinor: z.number().int(),
    })),
});
export const createTransactionRequestSchema = z.object({
    occurredOn,
    direction: z.enum(TRANSACTION_DIRECTIONS),
    amountMinor,
    currency,
    categoryId: z.string().uuid(),
    /** Absent or null is Overhead, and saves without complaint. */
    eventId: z.string().uuid().nullish(),
    counterparty: counterparty.nullish(),
    description,
});
/**
 * Absent leaves a field alone; `null` clears the ones that can be cleared.
 *
 * The same rule as every other PATCH in this API. Only `eventId` and
 * `counterparty` are nullable — a Transaction cannot be stripped of its date,
 * direction, amount, currency, category or description and remain a record of
 * anything.
 */
export const updateTransactionRequestSchema = z.object({
    occurredOn: occurredOn.optional(),
    direction: z.enum(TRANSACTION_DIRECTIONS).optional(),
    amountMinor: amountMinor.optional(),
    currency: currency.optional(),
    categoryId: z.string().uuid().optional(),
    eventId: z.string().uuid().nullish(),
    counterparty: counterparty.nullish(),
    description: description.optional(),
});
/**
 * Which half of the reconciliation to list — STORY-023.
 *
 * The Overhead figure must never be an unexplained lump: activating it lists
 * the Transactions that compose it. `attributed` is the other half, so the
 * two drill-downs together are the whole ledger and the identity on the
 * summary can be walked from either end.
 */
export const TRANSACTION_ATTRIBUTIONS = ['attributed', 'overhead'];
export const listTransactionsQuerySchema = z
    .object({
    /** One Event's money. Omitted lists everything, attributed and overhead. */
    eventId: z.string().uuid().optional(),
    /** Naming an Event, or naming none. Omitted lists both. */
    attribution: z.enum(TRANSACTION_ATTRIBUTIONS).optional(),
    /**
     * One currency's rows — STORY-023's drill-down needs it.
     *
     * The reconciliation figures are strictly per currency, so a drill-down
     * that is not would produce a list adding up to no figure on the screen
     * that opened it. That is worse than no drill-down: it invites the check
     * and then fails it.
     */
    currency: currency.optional(),
    direction: z.enum(TRANSACTION_DIRECTIONS).optional(),
    categoryId: z.string().uuid().optional(),
    /** Inclusive, in the Organisation's timezone — the day, not the instant. */
    from: occurredOn.optional(),
    to: occurredOn.optional(),
    limit: z.number().int().min(1).max(TRANSACTIONS_PAGE_MAX).optional(),
    offset: z.number().int().min(0).optional(),
})
    /* Refused rather than answered with an empty list. "Overhead, within this
     * Event" is a contradiction — nothing can satisfy it — and returning nothing
     * would read as "that night cost you nothing", which is a plausible answer
     * and therefore the worse failure. */
    .refine((query) => !(query.eventId !== undefined && query.attribution === 'overhead'), 'An Event cannot be asked for its overhead — overhead is what names no Event.');
/**
 * What one Event made — STORY-022.
 *
 * Everything is an integer of minor units, and nothing here is a percentage.
 * The two sides of each comparison travel and the screen divides them, so the
 * rounding happens once — computing a percentage here and formatting it there
 * would round twice.
 */
export const eventMoneyResponseSchema = z.object({
    /**
     * One entry per currency, ordered by code. Empty when nothing has been
     * recorded, which is most Events most of the time and is not an error.
     */
    figures: z.array(z.object({
        currency: z.string(),
        incomeMinor: z.number().int(),
        expenseMinor: z.number().int(),
        /** Income minus expense. Negative is a loss, and is a real answer. */
        profitMinor: z.number().int(),
    })),
    /**
     * Spend against the planned budget, in ONE currency.
     *
     * Null when no budget is set — which is not a budget of zero. "Not set" and
     * "0% used" are different facts and the second is a lie about the first, so
     * the client says so rather than dividing.
     */
    budget: z
        .object({
        currency: z.string(),
        budgetMinor: z.number().int(),
        spentMinor: z.number().int(),
    })
        .nullable(),
    /** Actual income against what was expected, in ONE currency. */
    revenue: z
        .object({
        currency: z.string(),
        expectedMinor: z.number().int(),
        actualMinor: z.number().int(),
    })
        .nullable(),
});
/**
 * Attributed + Overhead = Organisation — STORY-023.
 *
 * The three figures are shown and labelled rather than one being derived on
 * screen, because the whole point is that the arithmetic is VISIBLE. A
 * spreadsheet has these numbers too; what it does not have is the middle line
 * that explains why the other two differ.
 *
 * Per currency, and never summed across. The identity holds within each one.
 */
export const reconciliationResponseSchema = z.object({
    figures: z.array(z.object({
        currency: z.string(),
        /** Profit of every Transaction naming an Event. */
        attributedMinor: z.number().int(),
        /** Profit of every Transaction naming none. May be positive. */
        overheadMinor: z.number().int(),
        /** The two above, and independently counted — they must agree. */
        organisationMinor: z.number().int(),
    })),
});
/**
 * The nine headline metrics — STORY-024.
 *
 * Every figure carries its **basis** on the wire, not only its value:
 * `eventCount` beside an average, `budgetMinor` beside spend, a list of
 * Events beside a top figure. "Average profit (9 Events)" is honest; "Average
 * profit" alone is how a wrong number survives for months, and a client cannot
 * state a basis it was never sent.
 *
 * No percentage crosses this boundary. Both sides of every ratio travel as
 * integer minor units and the division happens once, at display — computing it
 * on the server and formatting it on the client rounds twice, and the second
 * rounding is invisible.
 */
export const dashboardResponseSchema = z.object({
    counts: z.object({
        /** Visibly the other three added together. A bare number means nothing. */
        total: z.number().int(),
        /** `planned` and `confirmed`. An idea is not a commitment. */
        upcoming: z.number().int(),
        completed: z.number().int(),
        /** Ideas and cancellations — named, so nothing falls out of every count. */
        uncounted: z.number().int(),
    }),
    /**
     * Everything recorded, per currency — three of the nine metrics.
     *
     * Not derived from `eventProfits`: those exclude overhead and exclude
     * cancelled Events, so their sum is a smaller number that looks exactly like
     * a total. `profitMinor` here is the same figure STORY-023's reconciliation
     * calls the organisation total, and a test asserts the two agree — otherwise
     * two tabs of one screen would disagree by exactly the overhead, which is
     * the spreadsheet defect this product exists to fix.
     */
    totals: z.array(z.object({
        currency: z.string(),
        incomeMinor: z.number().int(),
        expenseMinor: z.number().int(),
        profitMinor: z.number().int(),
    })),
    /**
     * Every Event that ran, with its profit — what the diverging bar draws.
     *
     * Carried rather than reconstructed: the chart's job is to show the SPREAD
     * the average sits in, and a spread cannot be rebuilt from an average and a
     * maximum.
     */
    eventProfits: z.array(z.object({
        eventId: z.string(),
        name: z.string(),
        status: z.enum(EVENT_STATUSES),
        currency: z.string(),
        profitMinor: z.number().int(),
    })),
    averageProfit: z.array(z.object({
        currency: z.string(),
        /** How many Events it averaged. Cancelled and Idea are not among them. */
        eventCount: z.number().int(),
        totalMinor: z.number().int(),
        averageMinor: z.number().int().nullable(),
    })),
    mostProfitable: z.array(z.object({
        currency: z.string(),
        profitMinor: z.number().int(),
        /** Every tied Event. A single winner is what a `LIMIT 1` quietly is. */
        events: z.array(z.object({ eventId: z.string(), name: z.string() })),
    })),
    utilisation: z.array(z.object({
        currency: z.string(),
        spentMinor: z.number().int(),
        /** Planned budget, never expected revenue. Null reads "not set". */
        budgetMinor: z.number().int().nullable(),
    })),
});
//# sourceMappingURL=transactions.js.map